import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Accordion,
  AccordionDetails,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  AccordionSummary,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import { useHistory, useLocation } from 'react-router-dom'
import {
  useImpactModels,
  useImpactModelProfiles,
  useTransformsForAdaptations,
  useCreateSlotProfile,
  useDeleteSlotProfile,
  useUpdateSlotProfileTransform,
  useBaseTransforms,
  useTransformById,
} from '../hooks/climateAnalytics'
import { usePropertyTypes } from '../hooks/locations'
import api from 'balkerne-core/api'
import { useQuery } from 'react-query'
import siteMap from '../siteMap'

// ---- Types -------------------------------------------------------------------------
type UUID = string
interface Transform {
  id: number
  label: string
  description?: string | null
  hazard: string
}

interface Slot {
  id: number
  ref: string
  label: string
  description?: string | null
  preferred_tags?: Record<string, string> | null
  default_transform_id: number | null
  optional: boolean
}

interface ImpactModelKey {
  hazard: string
  name: string
  uid: string
}

interface ImpactModel {
  id: number
  hazard_id: number
  key: ImpactModelKey
  label: string
  description?: string
  slots: Slot[]
}

interface Profile {
  id: number
  property_type_id: UUID
  description?: string | null
  created_at?: string
  transform: Transform
}

// Response shape for profiles endpoint
interface ProfilesPayload {
  model: ImpactModel
  slot_profiles_by_type_id: Record<
    string, // slotRef
    Record<UUID, Profile>
  >
}

function useQueryParam(key: string) {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search).get(key), [search, key])
}

const ClimateAnalyticsConfigure = () => {
  const history = useHistory()
  const { data: models, isLoading, isError, error } = useImpactModels()

  console.log(models)

  // Local search/filter state
  const [search, setSearch] = useState('')
  const filteredModels = useMemo(() => {
    if (!models) return []
    const q = search.trim().toLowerCase()
    if (!q) return models
    return models.filter((m: ImpactModel) => [m.label, m.description].some(v => v?.toLowerCase().includes(q)))
  }, [models, search])

  const [selectedUID, setSelectedUID] = useState<string | null>(null)
  const modelUID = useQueryParam('model')

  // Sync selected model with URL param
  useEffect(() => {
    if (modelUID) setSelectedUID(modelUID)
  }, [modelUID])

  // Default to first model if none selected
  useEffect(() => {
    if (!selectedUID && models && models.length) {
      setSelectedUID(models[0].key.uid)
      history.replace({ search: `?model=${encodeURIComponent(models[0].key.uid)}` })
    }
  }, [selectedUID, models, history])

  const selectedModel: ImpactModel | undefined = useMemo(
    () => models?.find((m: ImpactModel) => m.key.uid === selectedUID),
    [models, selectedUID],
  )

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { md: '360px 1fr' }, minHeight: '100%' }}>
      {/* Left: Models list */}
      <Box sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' }, p: 2 }}>
        <Toolbar disableGutters sx={{ mb: 1, gap: 1 }}>
          <IconButton size="small" onClick={() => history.push(siteMap.ClimateAnalytics.path)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Configure Vulnerability
          </Typography>
        </Toolbar>

        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search models"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ mt: 1.5 }}>
          {isLoading && (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {isError && <Alert severity="error">Failed to load models: {String((error as any)?.message ?? error)}</Alert>}

          {!isLoading && !isError && (
            <List dense sx={{ pt: 0 }}>
              {filteredModels.map((m: ImpactModel) => {
                const active = selectedUID === m.key.uid
                return (
                  <ListItem
                    key={m.key.uid}
                    button
                    selected={active}
                    onClick={() => {
                      setSelectedUID(m.key.uid)
                      history.push({ search: `?model=${encodeURIComponent(m.key.uid)}` })
                    }}
                    sx={{ borderRadius: 1, mb: 0.5 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'transparent', border: '1px solid', borderColor: 'divider' }}>
                        {m.label.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: active ? 700 : 600 }}>
                          {m.label}
                        </Typography>
                      }
                      secondary={m.description}
                    />
                  </ListItem>
                )
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Right: Details */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {selectedModel ? (
          <ModelDetails model={selectedModel} />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
            <Typography color="text.secondary">Select a model to configure</Typography>
          </Stack>
        )}
      </Box>
    </Box>
  )
}

function ModelDetails({ model }: { model: ImpactModel }) {
  const { data: modelPropertyTypeProfiles, isLoading, isError, error } = useImpactModelProfiles(model.key.uid)
  const { data: propertyTypes, isLoading: ptLoading, isError: ptError } = usePropertyTypes()

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        {model.label}
      </Typography>
      {model.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {model.description}
        </Typography>
      )}

      {isLoading || ptLoading ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading configuration…
          </Typography>
        </Stack>
      ) : null}

      {isError && <Alert severity="error">Failed to load profiles: {String((error as any)?.message ?? error)}</Alert>}
      {ptError && <Alert severity="error">Failed to load property types.</Alert>}

      {modelPropertyTypeProfiles && propertyTypes?.length ? (
        <Stack spacing={2}>
          {model.slots.map(slot => (
            <SlotConfiguration
              key={slot.id}
              slot={slot}
              payload={modelPropertyTypeProfiles as ProfilesPayload}
              propertyTypes={propertyTypes}
            />
          ))}
        </Stack>
      ) : null}

      {!isLoading && modelPropertyTypeProfiles && model.slots.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This model has no configurable slots.
        </Alert>
      )}
    </Box>
  )
}

function SlotConfiguration({
  slot,
  payload,
  propertyTypes,
}: {
  slot: Slot
  payload: ProfilesPayload
  propertyTypes: Array<{ id: UUID; label: string }>
}) {
  const { data: baseTransforms, isLoading: btLoading } = useBaseTransforms(slot.id, {})
  const profilesForSlot = payload.slot_profiles_by_type_id?.[slot.ref] ?? {}
  const assignedTypeIds = new Set(Object.keys(profilesForSlot))
  const addableTypes = propertyTypes.filter(pt => !assignedTypeIds.has(pt.id))

  const propertyTypeLabelMap = React.useMemo(
    () => Object.fromEntries(propertyTypes.map(pt => [pt.id, pt.label])),
    [propertyTypes],
  )

  const defaultTransform = useMemo(() => {
    if (typeof slot.default_transform_id !== 'number') return null
    return baseTransforms.find(t => t.id === slot.default_transform_id) ?? null
  }, [baseTransforms, slot.default_transform_id])

  const requiresExplicitTransform = slot.optional && typeof slot.default_transform_id !== 'number'

  if (btLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading slot…
        </Typography>
      </Stack>
    )
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {slot.label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" variant="outlined" label={slot.ref} />
            {slot.optional && <Chip size="small" variant="outlined" color="default" label="Optional" />}
            <Typography variant="caption" color="text.secondary">
              Default:{' '}
              {defaultTransform?.label ??
                (typeof slot.default_transform_id === 'number' ? `ID ${slot.default_transform_id}` : '—')}
            </Typography>
          </Stack>
          {slot.description && (
            <Typography variant="body2" color="text.secondary">
              {slot.description}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Profiles by property type
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {Object.entries(profilesForSlot).length === 0 && (
                <Alert severity="info">
                  {requiresExplicitTransform
                    ? 'No custom profiles yet. This slot has no default transform and will have no effect until you add a profile.'
                    : "No custom profiles yet. Unassigned property types will use the slot's default transform."}
                </Alert>
              )}
              {Object.entries(profilesForSlot).map(([ptId, profile]) => (
                <ProfileRow
                  key={ptId}
                  profile={profile as Profile}
                  baseTransforms={baseTransforms}
                  propertyTypeLabelMap={propertyTypeLabelMap}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Add profile
            </Typography>
            <AddProfileRow
              slotId={slot.id}
              addableTypes={addableTypes}
              baseTransforms={baseTransforms}
              requiresExplicitTransform={requiresExplicitTransform}
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

// ---- Profile Row ------------------------------------------------------------------
function ProfileRow({
  profile,
  baseTransforms,
  propertyTypeLabelMap,
}: {
  profile: Profile
  baseTransforms: Transform[]
  propertyTypeLabelMap: Record<UUID, string>
}) {
  const [openConfirm, setOpenConfirm] = useState(false)
  const [snack, setSnack] = useState<string | null>(null)

  const { mutate: updateTransform, isLoading: saving } = useUpdateSlotProfileTransform({
    onSuccess: () => setSnack('Profile updated'),
  })
  const { mutate: deleteProfile, isLoading: deleting } = useDeleteSlotProfile({
    onSuccess: () => setSnack('Profile removed (reverted to default)'),
  })

  const handleChange = (transformId: number) => {
    updateTransform({ profileId: profile.id, transformId })
  }

  const tfLabelById = useMemo(() => Object.fromEntries(baseTransforms.map(t => [t.id, t.label])), [baseTransforms])
  const selectedValue = profile.transform?.id ?? ''

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600 }}>
          {propertyTypeLabelMap[profile.property_type_id] ?? `${profile.property_type_id.slice(0, 8)}…`}
        </Typography>
        {profile.description && (
          <Typography variant="body2" color="text.secondary">
            {profile.description}
          </Typography>
        )}
      </Box>

      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel id={`transform-${profile.id}`}>Transform</InputLabel>
        <Select
          labelId={`transform-${profile.id}`}
          value={selectedValue}
          label="Transform"
          onChange={e => handleChange(Number(e.target.value))}
          disabled={saving}
          renderValue={val => {
            if (!val && val !== 0) return ''
            const id = Number(val)
            return tfLabelById[id] ?? String(val)
          }}>
          {baseTransforms.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title="Remove profile (use default)">
        <span>
          <IconButton color="error" onClick={() => setOpenConfirm(true)} disabled={deleting}>
            <DeleteOutlineIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Confirm dialog */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Remove profile</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the property type's profile. Model will fall back to the slot's default transformation for
            this property type.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setOpenConfirm(false)
              deleteProfile({ profileId: profile.id })
            }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={2000} onClose={() => setSnack(null)} message={snack ?? ''} />
    </Stack>
  )
}

// ---- Add Profile Row --------------------------------------------------------------
function AddProfileRow({
  slotId,
  addableTypes,
  baseTransforms,
  requiresExplicitTransform,
}: {
  slotId: number
  addableTypes: Array<{ id: UUID; label: string }>
  baseTransforms: Transform[]
  requiresExplicitTransform: boolean
}) {
  const [typeId, setTypeId] = useState<UUID>('')
  const [transformId, setTransformId] = useState<number | null>(null)
  const [description, setDescription] = useState('')
  const [snack, setSnack] = useState<string | null>(null)

  const { mutate: createProfile, isLoading } = useCreateSlotProfile({
    onSuccess: () => {
      setSnack('Profile added')
      setTypeId('')
      setTransformId(null)
      setDescription('')
    },
  })
  const canSubmit = typeId !== '' && (!requiresExplicitTransform || transformId !== null)
  const tfLabelById = useMemo(() => Object.fromEntries(baseTransforms.map(t => [t.id, t.label])), [baseTransforms])
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
      {/* property type select unchanged */}
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id={`pt-${slotId}`}>Property type</InputLabel>
        <Select
          labelId={`pt-${slotId}`}
          value={typeId}
          label="Property type"
          disabled={addableTypes.length === 0}
          onChange={e => setTypeId(e.target.value as UUID)}>
          {addableTypes.map(pt => (
            <MenuItem key={pt.id} value={pt.id}>
              {pt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: 260 }}>
        <InputLabel id={`tf-${slotId}`}>Transform</InputLabel>
        <Select
          labelId={`tf-${slotId}`}
          value={transformId}
          renderValue={val => {
            if (val === null) return ''
            return tfLabelById[val] ?? String(val)
          }}
          label="Transform"
          onChange={e => setTransformId(Number(e.target.value))}>
          {baseTransforms.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        placeholder="Description (optional)"
        value={description}
        onChange={e => setDescription(e.target.value)}
        sx={{ flex: 1, minWidth: 220 }}
      />

      <Tooltip title={canSubmit ? 'Create profile' : 'Select type and transform'}>
        <span>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canSubmit || isLoading}
            onClick={() =>
              createProfile({ slotId, propertyTypeId: typeId, transformId, description: description || null })
            }>
            Add
          </Button>
        </span>
      </Tooltip>

      <Snackbar open={Boolean(snack)} autoHideDuration={2000} onClose={() => setSnack(null)} message={snack ?? ''} />
    </Stack>
  )
}

export default ClimateAnalyticsConfigure
